import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from '@/components/ui/pagination'
import { cn } from '@/lib/utils'
import type { IntuiPaginationProps } from './types'

/**
 * Figma: Caliper Design System — Pagination
 *
 * Container: flex gap-1 items-center
 * Page items: size-8 (32px) rounded-md, text-sm font-medium
 * Active page: border border-input bg-background
 * Prev/Next: ghost buttons, disabled at boundaries (opacity-50)
 * Ellipsis: MoreHorizontal icon
 */

/** Build the list of page numbers and ellipsis markers for the pagination bar. */
function getPageRange(
  page: number,
  totalPages: number,
  siblingCount: number
): (number | 'ellipsis')[] {
  // When everything fits, just list all pages
  const totalSlots = siblingCount * 2 + 5 // siblings + first + last + 2 ellipsis + active
  if (totalPages <= totalSlots) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const leftSibling = Math.max(page - siblingCount, 1)
  const rightSibling = Math.min(page + siblingCount, totalPages)

  const showLeftEllipsis = leftSibling > 2
  const showRightEllipsis = rightSibling < totalPages - 1

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftCount = siblingCount * 2 + 3
    const leftRange = Array.from({ length: leftCount }, (_, i) => i + 1)
    return [...leftRange, 'ellipsis', totalPages]
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    const rightCount = siblingCount * 2 + 3
    const rightRange = Array.from(
      { length: rightCount },
      (_, i) => totalPages - rightCount + 1 + i
    )
    return [1, 'ellipsis', ...rightRange]
  }

  // Both ellipses
  const middleRange = Array.from(
    { length: rightSibling - leftSibling + 1 },
    (_, i) => leftSibling + i
  )
  return [1, 'ellipsis', ...middleRange, 'ellipsis', totalPages]
}

function IntuiPagination({
  page,
  totalPages,
  onPageChange,
  siblingCount = 1,
  className,
}: IntuiPaginationProps) {
  if (totalPages < 1) return null

  const pages = getPageRange(page, totalPages, siblingCount)
  const isFirstPage = page <= 1
  const isLastPage = page >= totalPages

  return (
    <Pagination className={cn(className)}>
      <PaginationContent>
        {/* Previous */}
        <PaginationItem>
          <PaginationPrevious
            onClick={(e) => {
              e.preventDefault()
              if (!isFirstPage) onPageChange(page - 1)
            }}
            className={cn(isFirstPage && 'pointer-events-none opacity-50')}
            aria-disabled={isFirstPage}
            tabIndex={isFirstPage ? -1 : undefined}
          />
        </PaginationItem>

        {/* Page numbers + ellipsis */}
        {pages.map((item, idx) =>
          item === 'ellipsis' ? (
            <PaginationItem key={`ellipsis-${idx}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={item}>
              <PaginationLink
                isActive={item === page}
                onClick={(e) => {
                  e.preventDefault()
                  if (item !== page) onPageChange(item)
                }}
              >
                {item}
              </PaginationLink>
            </PaginationItem>
          )
        )}

        {/* Next */}
        <PaginationItem>
          <PaginationNext
            onClick={(e) => {
              e.preventDefault()
              if (!isLastPage) onPageChange(page + 1)
            }}
            className={cn(isLastPage && 'pointer-events-none opacity-50')}
            aria-disabled={isLastPage}
            tabIndex={isLastPage ? -1 : undefined}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}

export { IntuiPagination, getPageRange }
